import {
  ApiResponse,
  PartnershipMember,
  PartnershipTotalsFilter,
  TotalItem,
} from "@axl-dev/school-sdk";
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ErrorCode } from "@app/shared/lib/enums/error-code";
import { useSdk } from "@app/shared/lib/hooks";

type PartnershipContextType = {
  currentMember: PartnershipMember | null;
  loading: {
    root: boolean;
    accepting: boolean;
    fetchingTotals: boolean;
    fetchingMaxAvailableAmount: boolean;
  };
  acceptAgreement: () => Promise<ApiResponse<unknown>>;
  fetchTotals: (filter: PartnershipTotalsFilter) => Promise<
    ApiResponse<{
      items: TotalItem[];
    }>
  >;
  fetchMaxAvailableAmount: () => Promise<ApiResponse<number>>;
};

const PartnershipContext = createContext<PartnershipContextType>(
  {} as PartnershipContextType,
);

export const usePartnership = () => useContext(PartnershipContext);

export const PartnershipProvider: FC<PropsWithChildren> = ({ children }) => {
  const sdk = useSdk();
  const [loading, setLoading] = useState({
    root: true,
    accepting: false,
    fetchingTotals: false,
    fetchingMaxAvailableAmount: false,
  });
  const [currentMember, setCurrentMember] = useState<PartnershipMember | null>(
    null,
  );

  useEffect(() => {
    (async () => {
      setLoading((prev) => ({ ...prev, root: true }));
      const result = await sdk.partnership.memberStore.fetchItem("");
      // not found it's ok
      if (
        !result.success &&
        !result.errors.find(
          (c) => c.code === ErrorCode.PartnershipMemberNotFound,
        )
      ) {
        setLoading((prev) => ({ ...prev, root: false }));

        return;
      }

      const member = result.body ?? new PartnershipMember();
      // auto create member
      if (!member.id) {
        const createResult = await sdk.partnership.memberStore.create(member);
        if (!createResult.success) {
          setLoading((prev) => ({ ...prev, root: false }));

          return;
        }
      }

      setCurrentMember(member);
      setLoading((prev) => ({ ...prev, root: false }));
    })();
  }, [sdk.partnership.memberStore]);
  const acceptAgreement = useCallback(async () => {
    if (!currentMember) throw new Error();
    setLoading((prev) => ({ ...prev, accepting: true }));
    try {
      return sdk.partnership.memberStore.acceptAgreement(currentMember);
    } finally {
      setLoading((prev) => ({ ...prev, accepting: false }));
    }
  }, [currentMember, sdk.partnership.memberStore]);

  const fetchTotals = useCallback(
    async (filter: PartnershipTotalsFilter) => {
      setLoading((prev) => ({ ...prev, fetchingTotals: true }));
      try {
        return await sdk.api.partnership.fetchTotals(filter);
      } finally {
        setLoading((prev) => ({ ...prev, fetchingTotals: false }));
      }
    },
    [sdk.api.partnership],
  );

  const fetchMaxAvailableAmount = useCallback(async () => {
    setLoading((prev) => ({ ...prev, fetchingMaxAvailableAmount: true }));
    try {
      return await sdk.api.partnership.fetchMaxAmount();
    } finally {
      setLoading((prev) => ({ ...prev, fetchingMaxAvailableAmount: false }));
    }
  }, [sdk.api.partnership]);

  return (
    <PartnershipContext.Provider
      value={useMemo(
        () => ({
          loading,
          currentMember,
          fetchMaxAvailableAmount,
          fetchTotals,
          acceptAgreement,
        }),
        [
          acceptAgreement,
          currentMember,
          fetchMaxAvailableAmount,
          fetchTotals,
          loading,
        ],
      )}
    >
      {children}
    </PartnershipContext.Provider>
  );
};
